/**
 * Utilities for image cropping in Goonfolio
 */

export interface CropData {
  x: number // X position (percentage 0-100)
  y: number // Y position (percentage 0-100)
  width: number // Width (percentage 0-100)
  height: number // Height (percentage 0-100)
  zoom: number // Zoom level (1 = 100%)
  rotation?: number // Rotation in degrees (optional)
}

export interface CroppedImage {
  id: string
  postId: string
  cropData: CropData
  createdAt: string
  name?: string
}

/**
 * Calculate crop styles for rendering
 */
export function getCropStyles(cropData: CropData): React.CSSProperties {
  return {
    position: 'absolute',
    left: `-${cropData.x}%`,
    top: `-${cropData.y}%`,
    width: `${(100 / cropData.width) * 100}%`,
    height: `${(100 / cropData.height) * 100}%`,
    transform: `scale(${cropData.zoom})${cropData.rotation ? ` rotate(${cropData.rotation}deg)` : ''}`,
    transformOrigin: 'center',
  }
}

/**
 * Calculate container styles for cropped image
 */
export function getCropContainerStyles(): React.CSSProperties {
  return {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    position: 'relative',
  }
}

/**
 * Validate crop data
 */
export function isValidCropData(cropData: Partial<CropData>): cropData is CropData {
  return (
    typeof cropData.x === 'number' &&
    typeof cropData.y === 'number' &&
    typeof cropData.width === 'number' &&
    typeof cropData.height === 'number' &&
    typeof cropData.zoom === 'number' &&
    cropData.x >= 0 &&
    cropData.x <= 100 &&
    cropData.y >= 0 &&
    cropData.y <= 100 &&
    cropData.width > 0 &&
    cropData.width <= 100 &&
    cropData.height > 0 &&
    cropData.height <= 100 &&
    cropData.zoom > 0
  )
}

/**
 * Create default crop data (full image)
 */
export function getDefaultCropData(): CropData {
  return {
    x: 0,
    y: 0,
    width: 100,
    height: 100,
    zoom: 1,
  }
}

/**
 * Calculate aspect ratio constrained crop
 */
export function constrainToAspectRatio(
  cropData: CropData,
  aspectRatio: number | null, // width/height, null for free
): CropData {
  if (!aspectRatio) return cropData

  const currentRatio = cropData.width / cropData.height

  if (Math.abs(currentRatio - aspectRatio) < 0.01) {
    return cropData // Already correct
  }

  // Adjust to match aspect ratio
  if (currentRatio > aspectRatio) {
    // Too wide, reduce width
    const newWidth = cropData.height * aspectRatio
    return {
      ...cropData,
      width: Math.min(newWidth, 100),
    }
  } else {
    // Too tall, reduce height
    const newHeight = cropData.width / aspectRatio
    return {
      ...cropData,
      height: Math.min(newHeight, 100),
    }
  }
}

/**
 * Common aspect ratios
 */
export const ASPECT_RATIOS = {
  free: { label: 'Free', value: null },
  square: { label: '1:1', value: 1 },
  landscape: { label: '16:9', value: 16 / 9 },
  portrait: { label: '9:16', value: 9 / 16 },
  photo: { label: '4:3', value: 4 / 3 },
  wide: { label: '21:9', value: 21 / 9 },
} as const

/**
 * Generate unique crop ID
 */
export function generateCropId(): string {
  return `crop-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Calculate crop bounds to keep within image
 */
export function constrainCropBounds(cropData: CropData): CropData {
  const constrained = { ...cropData }

  // Ensure crop doesn't go outside image bounds
  if (constrained.x + constrained.width > 100) {
    constrained.x = 100 - constrained.width
  }
  if (constrained.y + constrained.height > 100) {
    constrained.y = 100 - constrained.height
  }

  // Ensure position is not negative
  constrained.x = Math.max(0, constrained.x)
  constrained.y = Math.max(0, constrained.y)

  return constrained
}

/**
 * Convert pixel coordinates to percentage
 */
export function pixelToPercent(
  pixelValue: number,
  containerSize: number,
): number {
  return (pixelValue / containerSize) * 100
}

/**
 * Convert percentage to pixel coordinates
 */
export function percentToPixel(
  percentValue: number,
  containerSize: number,
): number {
  return (percentValue / 100) * containerSize
}
